import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useBasket } from '~/features/basket';
import { LineItemViewModel, OrderTrackingModel, VoucherViewModel } from '~/lib/data-contract';
import { gtmProductMapper, useGtm } from '~/shared/utils';
import { useFranchise } from '~/shared/utils/franchise';
import { useProduct } from '~/shared/utils/product';
import { usePage } from '~/templates/pages';
import { GTMUserData } from '../lib/model';

export const useTracking = () => {
    const { trackCheckout, trackPurchase, trackCustomEvent } = useGtm();
    const { data: basket } = useBasket();
    const { asPath } = useRouter();
    const { data: product } = useProduct();
    const { activeMarket } = useFranchise();
    const { type } = usePage();
    const lines = useMemo(() => gtmProductMapper(basket?.lines || []), [basket?.lines]);
    const totalPrice = useMemo(() => basket?.rawTotalPrice?.toFixed(2) || '0.00', [basket?.rawTotalPrice]);
    const isCheckout = type === 'p100CheckoutPage';

    const trackCheckoutStep = (step: number, shippingMethodId?: string) => {
        if (!basket?.lines?.length) {
            return;
        }

        trackCheckout({
            basketId: basket.id || '',
            products: lines,
            step,
            currencyCode: activeMarket?.currency,
            shippingOption: shippingMethodId,
            totalPrice,
            totalQuantity: basket.lines.length.toString(),
        });
    };

    const trackCheckoutOrderStep = (props: {
        orderLines?: LineItemViewModel[];
        currencyCode?: string;
        paymentMethodUsed?: string;
    }) => {
        if (!props?.orderLines?.length) {
            return;
        }

        trackCheckout({
            basketId: basket?.id || '',
            products: gtmProductMapper(props?.orderLines),
            step: 4,
            currencyCode: props?.currencyCode,
            shippingOption: props?.paymentMethodUsed,
            totalPrice:  basket?.rawTotalPrice?.toFixed(2) || '0.00',
            totalQuantity: props.orderLines.length.toString()
        });
    };

    const trackOrderPurchase = (props: {
        order?: OrderTrackingModel;
        vouchers?: VoucherViewModel[];
        userData?: GTMUserData;
        paymentType?: string;
        shippingTier?: string;
        productLines?: LineItemViewModel[];
    }) => {
        if (!props?.order?.products?.length) {
            return;
        }

        const { id, revenue, currencyCode, shipping, tax } = props?.order || {};

        trackPurchase({
            orderId: id,
            products: gtmProductMapper(props.productLines),
            totalPrice: revenue,
            currencyCode: currencyCode,
            shippingPrice: shipping,
            vatPrice: tax,
            coupon: props?.vouchers?.map((voucher) => voucher.name || ''),
            userData: props?.userData,
            paymentType: props?.paymentType,
            shippingTier: props?.shippingTier,
        });
    };

    const trackClubSignUp = () => {
        const eventLabel = isCheckout ? 'checkout' : 'signup';
        trackCustomEvent({
            event: 'gaEvent',
            eventCategory: 'Kundeklub',
            eventAction: 'Sign-up',
            eventLabel,
        });
    };

    const trackPageView = () => {
        trackCustomEvent({
            event: 'pageview',
            pageURI: asPath,
        });
    };

    const trackProductReservation = () => {
        trackCustomEvent({
            event: 'gaEvent',
            eventCategory: 'Reserve & Collect',
            eventAction: 'Reserve - Product',
            eventLabel: product?.name,
        });
    };

    const trackStoreReservation = (storeName: string) => {
        trackCustomEvent({
            event: 'gaEvent',
            eventCategory: 'Reserve & Collect',
            eventAction: 'Reserve - Butik',
            eventLabel: storeName,
        });
    };

    const trackCheckoutEmailAdded = (email: string) => {
        trackCustomEvent({
            event: 'email_added',
            email_source: 'checkout_email',
            user_data: {
                email,
            },
        });
    };

    const trackAddressAddedInCheckout = (userData: GTMUserData) => {
        trackCustomEvent({
            event: 'address_added',
            email_source: 'checkout_address',
            user_data: userData,
        });
    };

    const trackNewMemberSignUpEmail = (email: string) => {
        trackCustomEvent({
            event: 'new_member',
            lead_source: 'become_club_member',
            user_data: {
                email,
            },
        });
    };

    const trackLogin = (userData: { email?: string; phone_number?: string }) => {
        trackCustomEvent({
            event: 'log_in',
            email_source: 'log_in',
            user_data: userData,
        });
    };

    const trackActivateAccount = (userData: {
        email?: string;
        phone_number?: string;
        address: { first_name?: string; last_name?: string }[];
        birthdate?: string;
    }) => {
        trackCustomEvent({
            event: 'new_member_step2',
            user_data: userData,
        });
    };

    return {
        trackCheckoutStep,
        trackCheckoutOrderStep,
        trackOrderPurchase,
        trackClubSignUp,
        trackPageView,
        trackProductReservation,
        trackStoreReservation,
        trackCheckoutEmailAdded,
        trackAddressAddedInCheckout,
        trackNewMemberSignUpEmail,
        trackLogin,
        trackActivateAccount,
    };
};
